import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, 
  CheckCircle2, 
  XCircle, 
  FileText, 
  CreditCard,
  UserPlus,
  Clock,
  CheckCheck,
  Trash2,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const NOTIFICATION_ICONS = {
  approval_requested: { icon: Clock, color: 'bg-amber-100 text-amber-600' },
  approval_granted: { icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-600' },
  approval_rejected: { icon: XCircle, color: 'bg-red-100 text-red-600' },
  task_assigned: { icon: FileText, color: 'bg-indigo-100 text-indigo-600' },
  task_completed: { icon: CheckCircle2, color: 'bg-green-100 text-green-600' },
  payment_received: { icon: CreditCard, color: 'bg-emerald-100 text-emerald-600' },
  payment_failed: { icon: CreditCard, color: 'bg-red-100 text-red-600' },
  system: { icon: Bell, color: 'bg-slate-100 text-slate-600' },
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalUnread, setTotalUnread] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [page]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [listRes, countRes] = await Promise.all([
        api.get(`/notifications?page=${page}&page_size=${pageSize}`),
        api.get('/notifications/unread-count'),
      ]);
      setNotifications(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setHasMore(listRes.data.has_more || false);
      setTotalUnread(countRes.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setTotalUnread(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.notification_id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setTotalUnread(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    try {
      const target = notifications.find(n => n.notification_id === notificationId);
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setTotal(prev => Math.max(0, prev - 1));
      if (target && !target.is_read) setTotalUnread(prev => Math.max(0, prev - 1));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (selectedTab === 'unread') return !n.is_read;
    if (selectedTab === 'read') return n.is_read;
    return true;
  });

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  return (
    <div className="space-y-8" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Notifications</h1>
          <p className="text-slate-600 dark:text-slate-400">
            {totalUnread > 0 ? `${totalUnread} unread notifications` : 'All caught up!'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {totalUnread > 0 && (
            <Button
              variant="outline"
              onClick={handleMarkAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all as read
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-6">
        {[
          { label: 'Unread', count: totalUnread, icon: Bell, color: 'indigo' },
          { label: 'Read', count: total - totalUnread, icon: CheckCheck, color: 'slate' },
          { label: 'Total', count: total, icon: Filter, color: 'slate' },
        ].map((stat) => (
          <Card key={stat.label} className="gds-card" data-testid={`notification-stat-${stat.label.toLowerCase()}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{stat.label}</p>
                  <p className="text-3xl font-bold font-heading mt-1">{stat.count}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Notifications List */}
      <Card className="gds-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <Tabs value={selectedTab} onValueChange={(v) => { setSelectedTab(v); setPage(1); }}>
              <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                <TabsTrigger value="all" className="rounded-lg">All</TabsTrigger>
                <TabsTrigger value="unread" className="rounded-lg">
                  Unread
                  {totalUnread > 0 && (
                    <Badge variant="destructive" className="ml-2">{totalUnread}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="read" className="rounded-lg">Read</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{selectedTab === 'unread' ? 'No unread notifications' : 'No notifications yet'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification, i) => {
                const typeConfig = NOTIFICATION_ICONS[notification.notification_type] || NOTIFICATION_ICONS.system;
                const TypeIcon = typeConfig.icon;
                
                return (
                  <motion.div
                    key={notification.notification_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.03 }}
                    className={`flex items-start gap-4 p-4 rounded-xl transition-colors cursor-pointer ${
                      notification.is_read 
                        ? 'bg-slate-50 dark:bg-slate-800/30' 
                        : 'bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-500'
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-item-${i}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
                      <TypeIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium ${!notification.is_read ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-slate-500 mt-2">
                            {formatTime(notification.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-indigo-500 rounded-full" />
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notification.notification_id);
                            }}
                            data-testid={`delete-notification-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Page {page} of {Math.ceil(total / pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
